import React, { useEffect, useMemo, useState } from 'react';
import Drawer from '@mui/material/Drawer';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import TableRightDrawer from 'src/components/TableRightDrawer';
import {
  confirmVehicleTrackingLoad,
  getDataForVehicleTrackingLoadCards,
  getLoadLineItemsForLoadHeader,
  getVehicleTrackingAttachedLoads,
} from 'src/redux/Actions/VehicleTrackingOutward';
import { ReactComponent as BackIcon } from 'src/Images/icons/common/backIcon.svg';
import { ReactComponent as LoadingImage } from 'src/Images/icons/vehicle-tracking/loading-image.svg';
import { ReactComponent as TargetInfoIcon } from 'src/Images/icons/common/target-info-icon.svg';
import { ReactComponent as BulletPoints } from 'src/Images/icons/common/bulletpoints.svg';
import { ReactComponent as DocumentIcon } from 'src/Images/icons/planning/Document.svg';
import { ReactComponent as PackQuantityIcon } from 'src/Images/icons/planning/PackQuantity.svg';
import { ReactComponent as DeliveryQuantityIcon } from 'src/Images/icons/planning/DeliveryQuantity.svg';
import { usePermissions } from 'src/utils/hooks/usePermissions';
import { ACTIONS } from 'src/utils/roles/actions';
import 'src/style/createVehicleTracking.css';

const CONFIRM_LOADING_STATUS_ID = 2;
const LOAD_LINE_ITEM_TABLE_PREFERENCE_KEY =
  'savedConfirmLoadingLoadLineItemColumns';

const DEFAULT_TABLE_FIELDS = [
  { label: 'Delivery Number', value: 'sap_code', key: 'sap_code' },
  { label: 'Delivery Date', value: 'delivery_date', key: 'delivery_date' },
  { label: 'Pack Type', value: 'pack_type', key: 'pack_type' },
  { label: 'Pack Quantity', value: 'pack_quantity', key: 'pack_quantity' },
  { label: 'Grade', value: 'grade', key: 'grade' },
  { label: 'Gross Weight', value: 'weight', key: 'weight' },
  {
    label: 'Product SAP Code',
    value: 'product_sap_code',
    key: 'product_sap_code',
  },
  {
    label: 'Product Description',
    value: 'product_description',
    key: 'product_description',
  },
  { label: 'Plant', value: 'plant', key: 'plant' },
  { label: 'Quantity', value: 'quantity', key: 'quantity' },
  { label: 'Batch', value: 'batch', key: 'batch' },
  { label: 'Status', value: 'status', key: 'status' },
];

const loadLineItemTablePreferences = () => {
  const saved = localStorage.getItem(LOAD_LINE_ITEM_TABLE_PREFERENCE_KEY);

  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      return {
        order: parsed.order || [],
        selected: parsed.selected || [],
      };
    } catch (e) {
      return {
        order: [],
        selected: [],
      };
    }
  }

  return {
    order: [],
    selected: [],
  };
};

const getLoadLineItemKey = (item) => item.id;
const getFirstValue = (...values) =>
  values.find((value) => value !== null && value !== undefined && value !== '');

const getNumericValue = (...values) => {
  const value = getFirstValue(...values);
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

function LoadLineItemSummaryCard({ Icon, label, value, uom, themeColor }) {
  const displayValue =
    typeof value === 'number' && uom !== 'Total' ? value.toFixed(2) : value;

  return (
    <div className="planning-dashboard-card div-flex-row div-space-between">
      <div className="div-flex-column rg-5 div-align-start">
        <span className="text-md text-semi-bold text-nowrap">{label}</span>
        <div className="div-flex-row cg-5 div-align-baseline">
          <span className="text-xxl text-medium-bold">{displayValue}</span>
          {uom && <span className="text-md text-grey">{uom}</span>}
        </div>
      </div>
      <div className="div-flex-column div-align-end">
        <span
          className={`delivery-summary-icon theme-text-color-${themeColor}`}
        >
          <Icon className="icon-size-xxxl" />
        </span>
      </div>
    </div>
  );
}

const getLoadLineItemColumnValue = (fieldKey, item) => {
  switch (fieldKey) {
    case 'delivery_date':
      return item.delivery_date;
    case 'product_sap_code':
      return item.product?.product_sap_code;
    case 'product_description':
      return item.product?.product_description;
    case 'plant':
      return item.plant?.name;
    case 'status':
      return item.status?.name;
    default:
      return item[fieldKey];
  }
};

const renderLoadLineItemColumnValue = (fieldKey, item) => {
  const value = getLoadLineItemColumnValue(fieldKey, item);

  return value !== null && value !== undefined && value !== '' ? value : '-';
};

export default function VehicleTrackingConfirmLoading({
  onBack,
  themeColor,
  vtLoadHeaderUniqueNumber,
}) {
  const { vehicleTrackingNumber } = useParams();
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();
  const canConfirmLoad = hasPermission(
    ACTIONS.VEHICLE_TRACKING.OUTWARD.CONFIRM_LOAD,
  );

  const authReducer = useSelector((state) => state.AuthReducer);
  const VehicleTrackingOutwardReducer = useSelector(
    (state) => state.VehicleTrackingOutwardReducer,
  );

  const tenantOrganisationId = authReducer.userData.organisations?.id;
  const { vehicleTrackingLoadDetails, loadLineItemsForLoadHeader } =
    VehicleTrackingOutwardReducer;

  const [selectedLoadLineItemIds, setSelectedLoadLineItemIds] = useState([]);

  const [isConfirming, setIsConfirming] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [activePackTypeSummaryKey, setActivePackTypeSummaryKey] =
    useState(null);

  const { order: initialOrder, selected: initialSelected } =
    loadLineItemTablePreferences();

  const [draggedFieldOrder, setDraggedFieldOrder] = useState(initialOrder);
  const [selectedFieldFilter, setSelectedFieldFilter] =
    useState(initialSelected);

  const loadLineItemList = useMemo(
    () =>
      loadLineItemsForLoadHeader?.lineItems ||
      loadLineItemsForLoadHeader?.line_items ||
      [],
    [loadLineItemsForLoadHeader],
  );
  const fieldFilter = useMemo(() => {
    const responseFields =
      loadLineItemsForLoadHeader?.fieldMapping ||
      loadLineItemsForLoadHeader?.field_mapping ||
      [];

    return responseFields.length > 0 ? responseFields : DEFAULT_TABLE_FIELDS;
  }, [loadLineItemsForLoadHeader]);

  const filteredFieldFilter = useMemo(() => {
    const source =
      draggedFieldOrder?.length > 0 ? draggedFieldOrder : selectedFieldFilter;

    return source
      .map((value) => fieldFilter.find((field) => field.value === value))
      .filter(Boolean);
  }, [draggedFieldOrder, fieldFilter, selectedFieldFilter]);

  useEffect(() => {
    if (
      !tenantOrganisationId ||
      !vehicleTrackingNumber ||
      !vtLoadHeaderUniqueNumber
    )
      return;

    dispatch(
      getDataForVehicleTrackingLoadCards(
        tenantOrganisationId,
        vehicleTrackingNumber,
        vtLoadHeaderUniqueNumber,
      ),
    );
  }, [
    dispatch,
    tenantOrganisationId,
    vehicleTrackingNumber,
    vtLoadHeaderUniqueNumber,
  ]);

  useEffect(() => {
    if (!tenantOrganisationId || !vtLoadHeaderUniqueNumber) return;

    dispatch(
      getLoadLineItemsForLoadHeader(
        tenantOrganisationId,
        vtLoadHeaderUniqueNumber,
      ),
    ).then((data) => {
      const lineItems = data?.lineItems || data?.line_items || [];
      setSelectedLoadLineItemIds(
        lineItems.map(getLoadLineItemKey).filter(Boolean),
      );
    });
  }, [dispatch, tenantOrganisationId, vtLoadHeaderUniqueNumber]);

  useEffect(() => {
    if (fieldFilter.length) {
      const fieldValues = fieldFilter.map((field) => field.value);
      const validSelectedFields = selectedFieldFilter.filter((fieldValue) =>
        fieldValues.includes(fieldValue),
      );
      const missingFields = fieldValues.filter(
        (fieldValue) => !validSelectedFields.includes(fieldValue),
      );

      if (
        selectedFieldFilter.length === 0 ||
        validSelectedFields.length !== selectedFieldFilter.length
      ) {
        setSelectedFieldFilter([...validSelectedFields, ...missingFields]);
      }
    }
  }, [fieldFilter, selectedFieldFilter]);

  useEffect(() => {
    localStorage.setItem(
      LOAD_LINE_ITEM_TABLE_PREFERENCE_KEY,
      JSON.stringify({
        order: draggedFieldOrder,
        selected: selectedFieldFilter,
      }),
    );
  }, [draggedFieldOrder, selectedFieldFilter]);

  const toggleDrawer = (open) => () => {
    setOpenDrawer(open);
  };

  const handleSelectedFieldFilter = (e, value) => {
    let updated = [...selectedFieldFilter];

    if (e.target.checked) {
      updated.push(value);
    } else {
      updated = updated.filter((fieldValue) => fieldValue !== value);
    }

    setSelectedFieldFilter(updated);
  };

  function handleLoadLineItemCheckboxChange(loadLineItemId, isChecked) {
    if (loadLineItemId === null || loadLineItemId === undefined) return;

    setSelectedLoadLineItemIds((prev) => {
      if (isChecked) {
        return prev.includes(loadLineItemId) ? prev : [...prev, loadLineItemId];
      }

      return prev.filter((key) => key !== loadLineItemId);
    });
  }

  function handleConfirmLoad() {
    if (!canConfirmLoad) return;

    setIsConfirming(true);

    const updateLoadStatusPayload = {
      tenant_organisation_id: tenantOrganisationId,
      vt_load_header_unique_number: vtLoadHeaderUniqueNumber,
      vehicle_tracking_load_line_item_ids: selectedLoadLineItems.map(
        (item) => item.id,
      ),
    };

    dispatch(confirmVehicleTrackingLoad(updateLoadStatusPayload))
      .then(() => {
        dispatch(
          getDataForVehicleTrackingLoadCards(
            tenantOrganisationId,
            vehicleTrackingNumber,
            vtLoadHeaderUniqueNumber,
          ),
        );
        dispatch(
          getVehicleTrackingAttachedLoads(
            tenantOrganisationId,
            vehicleTrackingNumber,
          ),
        );
        onBack();
      })
      .finally(() => {
        setIsConfirming(false);
      });
  }

  const statusName = vehicleTrackingLoadDetails?.status?.name || 'Confirm Load';
  const loadCode = vehicleTrackingLoadDetails?.loadCode || '--';
  const selectedLoadLineItems = loadLineItemList.filter((item) =>
    selectedLoadLineItemIds.includes(getLoadLineItemKey(item)),
  );

  const loadLineItemSelectionSummary = useMemo(() => {
    const packTypeSummaryMap = {};

    selectedLoadLineItems.forEach((item) => {
      const packTypeKey = item.pack_type ?? item.packType ?? 'unassigned';
      const packTypeName = item.pack_type ?? item.packType ?? 'Unassigned';

      if (!packTypeSummaryMap[packTypeKey]) {
        packTypeSummaryMap[packTypeKey] = {
          summaryKey: `pack-type-${packTypeKey}`,
          packTypeName,
          loadLineItemUniqueNumbers: new Set(),
          totalPackQuantity: 0,
          totalDeliveryQuantity: 0,
          totalGrossWeight: 0,
          quantityUOM:
            item?.unit_of_measurement?.name ||
            item?.unitOfMeasurement?.name ||
            vehicleTrackingLoadDetails?.loadingTotalQuantityUom,
        };
      }

      const loadLineItemId = getLoadLineItemKey(item);
      if (loadLineItemId !== null && loadLineItemId !== undefined) {
        packTypeSummaryMap[packTypeKey].loadLineItemUniqueNumbers.add(
          loadLineItemId,
        );
      }

      packTypeSummaryMap[packTypeKey].totalPackQuantity += getNumericValue(
        item.pack_quantity,
        item.packQuantity,
      );
      packTypeSummaryMap[packTypeKey].totalDeliveryQuantity += getNumericValue(
        item.quantity,
        item.actual_quantity,
        item.actualQuantity,
        item.receiving_quantity,
        item.receivingQuantity,
      );
      packTypeSummaryMap[packTypeKey].totalGrossWeight += getNumericValue(
        item.weight,
        item.gross_weight,
        item.grossWeight,
      );
    });

    return Object.values(packTypeSummaryMap).map((summary) => ({
      ...summary,
      totalDocuments: summary.loadLineItemUniqueNumbers.size,
    }));
  }, [selectedLoadLineItems, vehicleTrackingLoadDetails]);

  const activePackTypeSummary =
    loadLineItemSelectionSummary.find(
      (summary) => summary.summaryKey === activePackTypeSummaryKey,
    ) || loadLineItemSelectionSummary[0];

  const activeSummaryCards = [
    {
      label: 'Total Documents',
      key: 'total-documents',
      value: activePackTypeSummary?.totalDocuments,
      uom: 'Nos',
      Icon: DocumentIcon,
    },
    {
      label: 'Total Pack Quantity',
      key: 'total-pack-quantity',
      value: activePackTypeSummary?.totalPackQuantity,
      uom: 'Nos',
      Icon: PackQuantityIcon,
    },
    {
      label: 'Total Delivery Quantity',
      key: 'total-delivery-quantity',
      value: activePackTypeSummary?.totalDeliveryQuantity,
      uom: activePackTypeSummary?.quantityUOM,
      Icon: DeliveryQuantityIcon,
    },
    {
      label: 'Gross Weight',
      key: 'gross-weight',
      value: activePackTypeSummary?.totalGrossWeight,
      uom: activePackTypeSummary?.quantityUOM,
      Icon: DeliveryQuantityIcon,
    },
  ];
  const isConfirmLoadingStage =
    vehicleTrackingLoadDetails?.status?.id === CONFIRM_LOADING_STATUS_ID;
  const hasLoadLineItems = loadLineItemsForLoadHeader?.lineItems.length > 0;
  const confirmLoadEnabled =
    canConfirmLoad &&
    isConfirmLoadingStage &&
    hasLoadLineItems &&
    !isConfirming;

  return (
    <div className="div-flex-column rg-10 h-100 padding-top-10">
      {/* Header */}
      <div className="div-flex-row div-space-between div-align-center">
        <div className="div-flex-row cg-20 div-align-center">
          <div role="button" tabIndex={0} onClick={onBack} aria-label="Go back">
            <BackIcon className="icon-size-xxxl" />
          </div>
          <div className="div-flex-row cg-5 div-align-center">
            <LoadingImage className="icon-size-xxl" />
            <span className="text-lg text-medium-bold">{statusName}</span>
          </div>
          <span className="light-blue-pill text-md">{loadCode}</span>
        </div>
        <button
          className={`vehicle-data-form-submit-btn theme-background-${themeColor}`}
          onClick={handleConfirmLoad}
          disabled={!confirmLoadEnabled}
        >
          <span className="text-md">
            {isConfirming ? 'Confirming...' : 'Confirm Load'}
          </span>
        </button>
      </div>
      <div
        className={` div-flex-column rg-20  horizontal-padding-10 theme-scrollable-container-${themeColor}`}
      >
        <div className="div-flex-column rg-10">
          {loadLineItemSelectionSummary.length > 0 && (
            <>
              <div className="div-flex-row cg-5">
                {loadLineItemSelectionSummary.map((packTypeSummary) => {
                  const isActive =
                    packTypeSummary.summaryKey ===
                    activePackTypeSummary?.summaryKey;

                  return (
                    <button
                      key={packTypeSummary.summaryKey}
                      type="button"
                      className={`div-flex-row div-align-center text-sm ${
                        isActive
                          ? `theme-pill-active-${themeColor}`
                          : 'border border-radius-4 vertical-padding-5 horizontal-padding-10 text-grey'
                      }`}
                      onClick={() =>
                        setActivePackTypeSummaryKey(packTypeSummary.summaryKey)
                      }
                    >
                      <span>{packTypeSummary.packTypeName}</span>
                    </button>
                  );
                })}
              </div>
              <div className="div-flex-row cg-5 delivery-selection-summary-row">
                {activeSummaryCards.map((summaryCard) => (
                  <div
                    key={summaryCard.key}
                    className="delivery-selection-summary-item"
                  >
                    <LoadLineItemSummaryCard
                      Icon={summaryCard.Icon}
                      label={summaryCard.label}
                      value={summaryCard.value}
                      uom={summaryCard.uom}
                      themeColor={themeColor}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="bg-light-grey div-flex-row cg-5 div-align-center p-1">
          <TargetInfoIcon className="icon-size-sm" />
          <span className="text-md">
            Select the delivery orders below that you want to include before
            confirming the load.
          </span>
        </div>

        <div className="purchase-order-line-item-card border-radius-4">
          <div className="div-space-between div-align-center px-2 py-2 border-bottom">
            <span className="text-md text-medium-bold delivery-header">
              Delivery
            </span>
            <button
              className="icon-btn-none delivery-organiser-btn"
              onClick={toggleDrawer(true)}
              aria-label="Organiser"
              type="button"
            >
              <BulletPoints className="icon-size-lg" color="#9F9F9F" />
            </button>
            <Drawer
              anchor="right"
              open={Boolean(openDrawer)}
              onClose={toggleDrawer(false)}
              ModalProps={{
                keepMounted: false,
              }}
              sx={{ zIndex: 1 }}
            >
              <div className="right-drawer-component-small">
                <TableRightDrawer
                  toggleDrawer={toggleDrawer}
                  fieldFilter={fieldFilter}
                  selectedFieldFilter={selectedFieldFilter}
                  handleSelectedFieldFilter={handleSelectedFieldFilter}
                  setDraggedFieldOrder={setDraggedFieldOrder}
                  draggedFieldOrder={draggedFieldOrder}
                />
              </div>
            </Drawer>
          </div>
          <div
            className={`po-table-wrapper po-table-scroll-container theme-scrollable-container-${themeColor}`}
          >
            <table>
              <thead className="outward-delivery-table-head">
                <tr>
                  <th aria-label="Select load line item" />
                  {filteredFieldFilter.map((field) => (
                    <th key={field.value}>{field.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loadLineItemList.length > 0 ? (
                  loadLineItemList.map((item) => {
                    const loadLineItemId = getLoadLineItemKey(item);

                    return (
                      <tr key={item.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedLoadLineItemIds.includes(
                              loadLineItemId,
                            )}
                            disabled={
                              !loadLineItemsForLoadHeader?.vtLoadHeaderUniqueNumber
                            }
                            onChange={(e) =>
                              handleLoadLineItemCheckboxChange(
                                loadLineItemId,
                                e.target.checked,
                              )
                            }
                          />
                        </td>
                        {filteredFieldFilter.map((field) => (
                          <td key={field.value}>
                            {renderLoadLineItemColumnValue(field.key, item)}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={filteredFieldFilter.length + 1}
                      className="text-center text-grey"
                    >
                      No Load line items available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
